import { formatMealPlanDate } from "@/helpers/useCommonFunctions";
import { fetchDataFromAlgoliaById } from "@/services/algolia";
import { algoliaConfig } from "@/services/algolia/config";
import { setMealPlanData } from "@/utils/redux/mealPlan/mealPlanSlice";
import { getMealPlanRecipesAction } from "@/utils/redux/mealPlan/mealPlanSlice/actions";
import { getStartAndEndDatesForMealPlan } from "../common";
import { MealPlanDataItem } from "@/utils/types/recipes";
import { RECIPE_NOT_EXIST_DATA } from "@/utils/constant";

/**
 * Fetches meal plan data for the given date range and dispatches to the redux store.
 * - Requires a valid `Uid`, `gigyaToken`, and `currentDays` array.
 */
export const setAllMealPlanDataFunc = async (
  Uid: string | null,
  dispatch: any,
  token: any
) => {
  if (!Uid) return;
  const { startDate, endDate } = getStartAndEndDatesForMealPlan();
  try {
    const response = await dispatch(
      getMealPlanRecipesAction({
        start_date: formatMealPlanDate(startDate),
        end_date: formatMealPlanDate(endDate),
        token,
      })
    );
    await fetchAndUpdateMealData(response?.payload?.data || [], dispatch);
    return;
  } catch (error) {
    console.error("Error fetching meal plan data:", error);
  }
};

/**
 * Filters out duplicate recipes based on `recipe_id`,
 * and formats them into an array of Algolia object lookup payloads.
 */
const getUniqueRecipes = async (mealPlanRecipes: any) => {
  const uniqueRecipeIds = new Set();
  const payload = mealPlanRecipes
    .filter((recipe: any) => {
      if (uniqueRecipeIds.has(recipe.recipe_id)) return false;
      uniqueRecipeIds.add(recipe.recipe_id);
      return true;
    })
    .map((recipe: any) => ({
      indexName: algoliaConfig.indexNames.recipes,
      objectID: recipe.recipe_id,
    }));
  return payload;
};
/**
 * Updates the user's meal plan recipe data (fetched from the database based on start_date and expiration_date) in
 * Redux by retrieving additional details from Algolia.
 */

export const fetchAndUpdateMealData = async (
  mealPlanRecipes: any,
  dispatch: any,
  mealPlanRecipeList?: MealPlanDataItem[]
) => {
  if (!mealPlanRecipes?.length) return;
  const payload = await getUniqueRecipes(mealPlanRecipes);
  try {
    const response: any = await fetchDataFromAlgoliaById(payload);

    const validResults = (response?.results || []).filter(
      (item: any) => item !== null
    );

    const updatedMealSchedule = mealPlanRecipes.map((meal: any) => {
      const recipe = validResults.find(
        (item: any) => item?.objectID === meal?.recipe_id
      );
      return {
        ...meal,
        title: recipe?.title || `${RECIPE_NOT_EXIST_DATA}`, // Default fallback
        image:
          Array.isArray(recipe?.attachments) && recipe.attachments.length > 0
            ? recipe.attachments[0]
            : "", // Ensure attachments exists before accessing [0]
        featuredImage: recipe?.featuredImage || "",
        ingredients: recipe?.ingredients || [],
        totalTime: recipe?.cooking?.totalTime || "",
        pageSlug: recipe?.pageSlug,
      };
    });

    // Merge with existing state if it's an array, else use only new items
    let finalMealData: MealPlanDataItem[];

    if (Array.isArray(mealPlanRecipeList)) {
      finalMealData = [...mealPlanRecipeList, ...updatedMealSchedule];
    } else {
      finalMealData = [...updatedMealSchedule];
    }

    // Sort AFTER merging by meal_type_id
    const sortedFinalMealData = finalMealData.sort(
      (a, b) => a.meal_type_id - b.meal_type_id
    );

    // Dispatch final sorted meal plan data
    await dispatch(setMealPlanData(sortedFinalMealData));
  } catch (error) {
    console.error("Error fetching data from Algolia:", error);
  }
};
/**
 * Retrieves recipe data for selected meal plan recipe IDs, including additional recipe details from Algolia.
 */
export const fetchDataFromAlgolia = async (selectedMealPlanRecipes: any) => {
  if (!selectedMealPlanRecipes?.length) return;

  const payload = await getUniqueRecipes(selectedMealPlanRecipes);

  try {
    const response: any = await fetchDataFromAlgoliaById(payload);

    const validResults = (response?.results || []).filter(
      (item: any) => item !== null
    );

    const mealPlanResult = selectedMealPlanRecipes.map((meal: any) => {
      const recipe = validResults.find(
        (item: any) => item?.objectID === meal?.recipe_id
      );
      return {
        ...meal,
        title: recipe?.title || `${RECIPE_NOT_EXIST_DATA}`, // Default fallback
        pageSlug: recipe?.pageSlug,
        image:
          Array.isArray(recipe?.attachments) && recipe.attachments.length > 0
            ? recipe.attachments[0]
            : "", // Ensure attachments exists before accessing [0]
        featuredImage: recipe?.featuredImage || "",
        totalTime: recipe?.cooking?.totalTime || "",
      };
    });
    const sortedMealPlanResult = mealPlanResult.sort(
      (a: any, b: any) => a.meal_type_id - b.meal_type_id
    );

    return sortedMealPlanResult;
  } catch (error) {
    console.error("Error fetching data from Algolia:", error);
  }
};

/**
 * Fetches meal plan data for the given date range along with recipe details from algolia.
 * - Requires a valid `Uid`, `token`, `start_date` and `end_date`.
 * - Optionally accepts recipe_id
 */
export const fetchMealPlanDataFun = async (
  Uid: string | null,
  start_date: any,
  end_date: any,
  dispatch: any,
  token: any,
  recipe_id?: string | number
) => {
  if (!Uid || !token || !start_date || !end_date) return;

  try {
    const payload: any = {
      start_date: formatMealPlanDate(start_date),
      end_date: formatMealPlanDate(end_date),
      token: token,
    };

    if (recipe_id) {
      payload.recipe_id = recipe_id;
    }

    const response = await dispatch(getMealPlanRecipesAction(payload));
    if (response?.payload?.status == 401) {
      return { status: 401 };
    }
    const responseData = await fetchDataFromAlgolia(
      response?.payload?.data || []
    );
    return responseData;
  } catch (error) {
    console.error("Error fetching meal plan data:", error);
  }
};

/**
 * Groups & Categorizes meals under their respective scheduled dates and Meal Types.
 */

export const groupMealsByDateAndType = (mealPlanData: any) => {
  if (!Array.isArray(mealPlanData)) return [];

  // Sort by schedule_date in ascending order
  const sortedMealPlanData = [...mealPlanData].sort(
    (a, b) =>
      new Date(a.schedule_date).getTime() - new Date(b.schedule_date).getTime()
  );

  return sortedMealPlanData?.reduce((acc: any, meal: any) => {
    const dateKey = formatMealPlanDate(meal.schedule_date); // Format date for consistency
    const mealTypeKey = meal.meal_type_id;

    if (!acc[dateKey]) {
      acc[dateKey] = {}; // Initialize object for each unique date
    }

    if (!acc[dateKey][mealTypeKey]) {
      acc[dateKey][mealTypeKey] = []; // Initialize array for each meal type within date
    }
    acc[dateKey][mealTypeKey].push({
      id: meal.id,
      title: meal.title || `${RECIPE_NOT_EXIST_DATA}`,
      pageSlug: meal.pageSlug,
      image:
        Array.isArray(meal.image) && meal.image.length > 0 ? meal.image[0] : "",
      featuredImage: meal.featuredImage || "",
      totalTime: meal.totalTime || "",
    });

    return acc;
  }, {});
};

export const mapToMealCategories = (categories = []) => {
  return categories.map((name, index) => ({
    id: index + 1,
    name,
  }));
};

/**
 * Retrieves the meal category name based on the given ID from a raw category array.
 * id-1, Because data recieved from aem is an array starting from index 0;
 */
export const getMealCategoryName = (
  id: number | null,
  rawCategories: string[] = []
): string => {
  if (!id || id < 1 || id > rawCategories.length) return "Unknown";

  return rawCategories[id - 1] || "Unknown";
};

export function getMealsWithinRange(
  mealList: MealPlanDataItem[],
  startDate: Date | string,
  endDate: Date | string
): any[] {
  if (!Array.isArray(mealList)) return [];

  const startStr = formatMealPlanDate(startDate);
  const endStr = formatMealPlanDate(endDate);

  return mealList.filter((meal) => {
    const schedule_date = formatMealPlanDate(meal.schedule_date);
    return schedule_date >= startStr && schedule_date <= endStr;
  });
}
